/**
 * MessageFooterTabs (EXACT copy from IntelligenceStudioPage.jsx lines 246-835)
 */

import React, { useState, useMemo } from 'react';

import {
  ChevronDown,
  ChevronUp,
  MessageSquare,
  BookOpen,
  FileText,
  Quote,
  Sparkles,
  Loader2,
  Wand2,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';

import { CitationBadge } from '@/components/RichChat/citations';
import FileIcon from '@/components/smart-notebook/SmartTable/FileIcon';

// ==========================================
// ✅ ENTERPRISE: UNIFIED MESSAGE FOOTER TABS
// 3 Tabs: Follow-ups + Sources (Documents) + Citations (Chunks)
// ==========================================

const MessageFooterTabs = ({
  followUps: rawFollowUps = [],
  onFollowUpSelect,
  citations: rawCitations = [],
  sourceAttribution,
  onCitationClick,
  onGenerateFollowUps,
  isGeneratingFollowUps = false,
  messageId,
}) => {
  // ═══════════════════════════════════════════════════════════════════════════
  // ✅ WAVE 26 FIX: Ensure arrays are ALWAYS arrays (never objects/null)
  // ═══════════════════════════════════════════════════════════════════════════
  const followUps = Array.isArray(rawFollowUps) ? rawFollowUps : [];
  const citations = Array.isArray(rawCitations) ? rawCitations : [];

  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('followups');
  const [hasBeenOpened, setHasBeenOpened] = useState(false); // ✅ Track if user has opened this message's footer

  // ✅ Handle expand - remembers last tab if opened before, else defaults to 'followups'
  const handleExpand = (specificTab = null) => {
    if (specificTab) {
      // User clicked on a specific tab - go directly there
      setActiveTab(specificTab);
    } else if (!hasBeenOpened) {
      // First time opening - default to followups
      setActiveTab('followups');
    }
    // If hasBeenOpened and no specificTab, keep the current activeTab (remembered)

    setHasBeenOpened(true);
    setIsExpanded(true);
  };

  const hasFollowUps = followUps.length > 0;
  const hasCitations = citations.length > 0;

  // ═══════════════════════════════════════════════════════════════════════
  // ✅ WAVE 25: ARCHITECTURAL INVARIANT ENFORCEMENT (Frontend)
  // Rule 1: Citations with "Document" placeholder = INVALID (show warning)
  // Rule 2: Sources = unique VALID filenames only
  // Rule 3: If 0 valid sources AND >0 invalid citations → show data quality warning
  // ═══════════════════════════════════════════════════════════════════════

  // Helper to detect file type from filename
  const getFileTypeFromName = filename => {
    if (!filename) return 'document';
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    const typeMap = {
      pdf: 'pdf',
      xlsx: 'excel',
      xls: 'excel',
      csv: 'csv',
      docx: 'word',
      doc: 'word',
      pptx: 'powerpoint',
      ppt: 'powerpoint',
      jpg: 'image',
      jpeg: 'image',
      png: 'image',
    };
    return typeMap[ext] || 'document';
  };

  // Helper to check if a filename is a valid (not a placeholder)
  const isValidFileName = name => {
    return (
      name &&
      name !== 'Document' &&
      name !== 'document' &&
      name !== 'Unknown' &&
      !name.startsWith('Source ')
    );
  };

  // Build unique documents from sourceAttribution AND valid citations
  const buildUniqueDocuments = () => {
    const documentMap = {};

    // Step 1: Add from sourceAttribution (primary source of truth)
    if (sourceAttribution?.documentNames?.length > 0) {
      sourceAttribution.documentNames.filter(isValidFileName).forEach((name, idx) => {
        documentMap[name] = {
          name,
          index: idx + 1,
          citationCount: 0,
          fileType: getFileTypeFromName(name),
        };
      });
    }

    // Step 2: Derive from citations ONLY if they have valid filenames
    if (citations.length > 0) {
      citations.forEach(cite => {
        const docName =
          cite.sourceFile?.originalName ||
          cite.sourceFile?.name ||
          cite.display?.fileName ||
          cite.docName ||
          cite.fileName;

        if (isValidFileName(docName)) {
          if (!documentMap[docName]) {
            documentMap[docName] = {
              name: docName,
              index: Object.keys(documentMap).length + 1,
              citationCount: 1,
              fileType: cite.sourceFile?.type || getFileTypeFromName(docName),
            };
          } else {
            documentMap[docName].citationCount = (documentMap[docName].citationCount || 0) + 1;
          }
        }
      });
    }

    return Object.values(documentMap);
  };

  const uniqueDocuments = buildUniqueDocuments();

  // Count citations by validity (citations is guaranteed to be an array now)
  const validCitationsCount = citations.filter(c => {
    const fn = c.sourceFile?.name || c.sourceFile?.originalName || c.display?.fileName || c.docName;
    return isValidFileName(fn);
  }).length;

  const invalidCitationsCount = citations.length - validCitationsCount;

  // ARCHITECTURAL INVARIANT: If 0 valid sources, citations should also be 0
  // If we have invalid citations (with "Document" placeholder), they're orphaned
  const hasOrphanedCitations = uniqueDocuments.length === 0 && invalidCitationsCount > 0;

  const followUpsCount = followUps.length;
  const citationsCount = validCitationsCount; // Only count VALID citations
  const sourcesCount = uniqueDocuments.length;

  const hasSourceAttribution = sourcesCount > 0;
  const hasSources = sourcesCount > 0;

  // ✅ ENTERPRISE: Format citations with proper display
  const buildFormattedCitations = () => {
    if (citations.length === 0) return [];

    return citations.map((cite, idx) => {
      // Extract display info from enterprise citation format
      const display = cite.display || {};
      const sourceFile = cite.sourceFile || {};
      const location = cite.location || {};
      const verification = cite.verification || {};

      // ═══════════════════════════════════════════════════════════════════════
      // ✅ WAVE 25 FIX: Proper citation formatting with validation
      // ═══════════════════════════════════════════════════════════════════════

      // Extract filename - NEVER fallback to "Document"
      const extractedFileName =
        display.fileName ||
        sourceFile.originalName ||
        sourceFile.name ||
        cite.docName ||
        cite.fileName;
      const validFileName =
        extractedFileName && extractedFileName !== 'Document' && extractedFileName !== 'document'
          ? extractedFileName
          : `Source ${idx + 1}`; // Use numbered source as last resort, not generic "Document"

      // Build location string with full context
      let locationParts = [];
      if (location.pageNumber) locationParts.push(`Page ${location.pageNumber}`);
      if (location.sheetName) locationParts.push(location.sheetName);
      if (location.cellCoordinates) locationParts.push(`Cell ${location.cellCoordinates}`);
      if (location.rowNumber) locationParts.push(`Row ${location.rowNumber}`);
      if (location.slideNumber) locationParts.push(`Slide ${location.slideNumber}`);
      if (location.lineNumber) locationParts.push(`Line ${location.lineNumber}`);
      if (location.timestamp) locationParts.push(`${location.timestamp}s`); // Video timestamp

      const locationString =
        locationParts.length > 0
          ? locationParts.join(' • ')
          : display.locationString && display.locationString !== 'Document'
            ? display.locationString
            : null;

      // ✅ FIX: Cap confidence at 100%, handle both 0-1 and 0-100 formats
      let rawConfidence = display.confidence ?? verification.confidenceScore ?? 0.7;
      // Normalize: if value > 1, it's already a percentage; if <= 1, multiply by 100
      const normalizedConfidence =
        rawConfidence > 1 ? rawConfidence : Math.round(rawConfidence * 100);
      const cappedConfidence = Math.min(100, Math.max(0, normalizedConfidence));

      return {
        index: idx + 1,
        // ✅ User-friendly file name - NEVER "Document"
        fileName: validFileName,
        // ✅ Location string with rich context (Page, Sheet, Cell, Line, etc.)
        locationString: locationString,
        // ✅ Confidence capped at 100%
        confidence: cappedConfidence,
        // ✅ Citation type (direct_quote, paraphrase, inference, calculation)
        type: verification.type || 'inference',
        typeLabel: display.label || verification.typeInfo?.label || 'Inferred',
        // ✅ Color coding
        color:
          display.color ||
          (verification.type === 'direct_quote'
            ? 'green'
            : verification.type === 'paraphrase'
              ? 'blue'
              : 'orange'),
        icon:
          display.icon ||
          (verification.type === 'direct_quote'
            ? '🟢'
            : verification.type === 'paraphrase'
              ? '🔵'
              : '🟠'),
        // ✅ Preview text
        previewText: (cite.content?.exactText || cite.text || '').slice(0, 150),
        // ✅ File type for icon
        fileType: sourceFile.type || 'document',
        // ✅ Excel computation info if available
        computation: cite.computation || null,
        // ✅ Full location data for click-to-highlight
        locationData: location,
        // Original data for click handler
        raw: cite,
      };
    });
  };
  const formattedCitations = buildFormattedCitations();

  // ALWAYS render - uniform design for all AI messages

  return (
    <div className="transition-all duration-300 self-stretch">
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* COLLAPSED STATE - Compact clickable bar - matches message width */}
      {/* ✅ FIX: Each section is clickable and opens to that specific tab */}
      {/* ✅ FIX: Empty area opens to remembered tab or default (followups) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {!isExpanded && (
        <div
          className="flex items-center gap-0 text-xs bg-slate-100 dark:bg-neutral-800 border border-slate-200 dark:border-neutral-700 rounded-xl overflow-hidden w-full cursor-pointer"
          onClick={() => handleExpand()} // ✅ Empty area click - uses remembered tab
        >
          {/* Follow-ups - Clickable */}
          <button
            onClick={e => {
              e.stopPropagation();
              handleExpand('followups');
            }}
            className="flex items-center gap-2 px-3 py-2 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"
          >
            <Wand2 className="h-3.5 w-3.5 text-violet-500" />
            <span className="text-slate-600 dark:text-neutral-300">Follow-ups</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                followUpsCount > 0
                  ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                  : 'bg-slate-200 dark:bg-neutral-600 text-slate-400 dark:text-neutral-500'
              }`}
            >
              {followUpsCount}
            </span>
          </button>
          <div className="w-px h-6 bg-slate-300 dark:bg-neutral-600" />
          {/* Sources - Clickable */}
          <button
            onClick={e => {
              e.stopPropagation();
              handleExpand('sources');
            }}
            className="flex items-center gap-2 px-3 py-2 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors"
          >
            <FileText className="h-3.5 w-3.5 text-amber-500" />
            <span className="text-slate-600 dark:text-neutral-300">Sources</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                (uniqueDocuments.length || sourcesCount) > 0
                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                  : 'bg-slate-200 dark:bg-neutral-600 text-slate-400 dark:text-neutral-500'
              }`}
            >
              {uniqueDocuments.length || sourcesCount}
            </span>
          </button>
          <div className="w-px h-6 bg-slate-300 dark:bg-neutral-600" />
          {/* Citations - Clickable */}
          <button
            onClick={e => {
              e.stopPropagation();
              handleExpand('citations');
            }}
            className="flex items-center gap-2 px-3 py-2 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
          >
            <BookOpen className="h-3.5 w-3.5 text-emerald-500" />
            <span className="text-slate-600 dark:text-neutral-300">Citations</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                citationsCount > 0
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                  : 'bg-slate-200 dark:bg-neutral-600 text-slate-400 dark:text-neutral-500'
              }`}
            >
              {citationsCount}
            </span>
          </button>
          {/* Expand indicator - also clickable for empty area */}
          <div className="ml-auto px-3 py-2 text-slate-400 hover:bg-slate-200 dark:hover:bg-neutral-700 transition-colors flex-1 flex justify-end">
            <ChevronDown className="h-3.5 w-3.5" />
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* EXPANDED STATE - Full tabbed interface - matches message width */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {isExpanded && (
        <div className="border border-slate-200 dark:border-neutral-700 rounded-xl overflow-hidden bg-slate-50 dark:bg-neutral-800/50 shadow-lg w-full">
          {/* Tab Headers + Collapse Button */}
          <div className="flex items-stretch border-b border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
            {/* Tab 1: Follow-ups */}
            <button
              onClick={() => setActiveTab('followups')}
              className={`flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium transition-all flex-1 ${
                activeTab === 'followups'
                  ? 'text-violet-600 dark:text-violet-400 border-b-2 border-violet-500 bg-violet-50/50 dark:bg-violet-900/10'
                  : 'text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700/50'
              }`}
            >
              <Wand2 className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Follow-ups</span>
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                  activeTab === 'followups'
                    ? followUpsCount > 0
                      ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300'
                      : 'bg-slate-200 dark:bg-neutral-600 text-slate-400 dark:text-neutral-500'
                    : 'bg-slate-200 dark:bg-neutral-600 text-slate-600 dark:text-neutral-300'
                }`}
              >
                {followUpsCount}
              </span>
            </button>
            {/* Tab 2: Sources (Documents) */}
            <button
              onClick={() => setActiveTab('sources')}
              className={`flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium transition-all flex-1 ${
                activeTab === 'sources'
                  ? 'text-amber-600 dark:text-amber-400 border-b-2 border-amber-500 bg-amber-50/50 dark:bg-amber-900/10'
                  : 'text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700/50'
              }`}
            >
              <FileText className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Sources</span>
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                  activeTab === 'sources'
                    ? sourcesCount > 0
                      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
                      : 'bg-slate-200 dark:bg-neutral-600 text-slate-400 dark:text-neutral-500'
                    : 'bg-slate-200 dark:bg-neutral-600 text-slate-600 dark:text-neutral-300'
                }`}
              >
                {uniqueDocuments.length || sourcesCount}
              </span>
            </button>
            {/* Tab 3: Citations (Chunks) */}
            <button
              onClick={() => setActiveTab('citations')}
              className={`flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium transition-all flex-1 ${
                activeTab === 'citations'
                  ? 'text-emerald-600 dark:text-emerald-400 border-b-2 border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10'
                  : 'text-slate-500 dark:text-neutral-400 hover:text-slate-700 dark:hover:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700/50'
              }`}
            >
              <BookOpen className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Citations</span>
              <span
                className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] ${
                  activeTab === 'citations'
                    ? citationsCount > 0
                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                      : 'bg-slate-200 dark:bg-neutral-600 text-slate-400 dark:text-neutral-500'
                    : 'bg-slate-200 dark:bg-neutral-600 text-slate-600 dark:text-neutral-300'
                }`}
              >
                {citationsCount}
              </span>
            </button>
            {/* Collapse Button */}
            <button
              onClick={() => setIsExpanded(false)}
              className="px-3 py-2 text-slate-400 hover:text-slate-600 dark:hover:text-neutral-300 hover:bg-slate-100 dark:hover:bg-neutral-700/50 transition-colors border-l border-slate-200 dark:border-neutral-700 flex-shrink-0"
              title="Collapse"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
          </div>

          {/* Tab Content - Scrollable */}
          <div className="p-3 max-h-48 overflow-y-auto">
            {/* TAB 1: Follow-ups */}
            {activeTab === 'followups' && (
              <div>
                {hasFollowUps ? (
                  <div className="flex flex-wrap gap-2">
                    {followUps.map((suggestion, i) => {
                      // ✅ FIX: Handle both string and object {text, type} formats
                      const suggestionText =
                        typeof suggestion === 'string' ? suggestion : suggestion?.text || '';
                      return (
                        <button
                          key={i}
                          onClick={() => onFollowUpSelect?.(suggestionText)}
                          className="group flex items-center gap-1.5 px-3 py-2 text-xs bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 rounded-lg border border-violet-200 dark:border-violet-800 hover:bg-violet-100 dark:hover:bg-violet-900/40 hover:border-violet-300 dark:hover:border-violet-700 transition-all"
                        >
                          <span className="text-left">{suggestionText}</span>
                          <ArrowRight className="h-3 w-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-4 gap-3">
                    <div className="flex items-center text-xs text-slate-400 dark:text-neutral-500">
                      <Wand2 className="h-4 w-4 mr-2 opacity-50" />
                      <span>No follow-up suggestions available</span>
                    </div>
                    {onGenerateFollowUps && (
                      <button
                        onClick={() => onGenerateFollowUps(messageId)}
                        disabled={isGeneratingFollowUps}
                        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isGeneratingFollowUps ? (
                          <>
                            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="h-3.5 w-3.5" />
                            <span>Generate Follow-ups</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: Sources (Documents Only) */}
            {activeTab === 'sources' && (
              <div className="space-y-2">
                {uniqueDocuments.length > 0 ? (
                  <>
                    {/* Source Summary */}
                    <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300 pb-2 border-b border-slate-200 dark:border-neutral-700">
                      <FileText className="h-4 w-4 flex-shrink-0" />
                      <span className="font-medium">
                        {uniqueDocuments.length} document{uniqueDocuments.length !== 1 ? 's' : ''}{' '}
                        used in this response
                      </span>
                    </div>
                    {/* Document Cards */}
                    <div className="space-y-2">
                      {uniqueDocuments.map((doc, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 p-2 rounded-lg bg-white dark:bg-neutral-700/50 border border-slate-200 dark:border-neutral-600"
                        >
                          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 font-bold text-sm">
                            {i + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-slate-800 dark:text-neutral-200 truncate">
                              {doc.name}
                            </div>
                            {doc.citationCount > 0 && (
                              <div className="text-[10px] text-slate-500 dark:text-neutral-400">
                                {doc.citationCount} citation{doc.citationCount !== 1 ? 's' : ''}{' '}
                                from this file
                              </div>
                            )}
                          </div>
                          <div className="flex-shrink-0">
                            <FileIcon ext={doc.name?.split('.').pop() || ''} className="w-7 h-7" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center py-4 text-xs text-slate-400 dark:text-neutral-500">
                    <FileText className="h-4 w-4 mr-2 opacity-50" />
                    <span>No sources referenced in this response</span>
                  </div>
                )}
              </div>
            )}

            {/* TAB 3: Citations (Detailed Chunks) */}
            {activeTab === 'citations' && (
              <div className="space-y-2">
                {/* ⚠️ WAVE 25: Warning for orphaned citations (no valid source linkage) */}
                {hasOrphanedCitations && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg mb-2">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-amber-700 dark:text-amber-300">
                        <span className="font-medium">Data Quality Notice:</span> This response
                        contains {invalidCitationsCount} citation(s) without valid source linkage.
                        These may have been generated before the citation system was upgraded.
                        <span className="block mt-1 text-amber-600 dark:text-amber-400">
                          💡 Ask a new question to get properly linked citations.
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {formattedCitations.length > 0 ? (
                  <>
                    {/* Citation Legend */}
                    <div className="flex items-center gap-3 text-[10px] text-slate-500 dark:text-neutral-400 pb-2 border-b border-slate-200 dark:border-neutral-700">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        Verbatim
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        Summarized
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                        Inferred
                      </span>
                    </div>
                    {/* Citation Cards - Only show VALID citations */}
                    <div className="space-y-2">
                      {formattedCitations
                        .filter(
                          cite =>
                            cite.fileName &&
                            cite.fileName !== 'Document' &&
                            cite.fileName !== 'Unknown'
                        )
                        .map((cite, i) => (
                          <CitationBadge
                            key={cite.raw?.citationId || i}
                            citation={cite.raw}
                            index={i}
                            onClick={cit => onCitationClick?.(cit)}
                            onGoToSource={cit => onCitationClick?.(cit)}
                            showHoverCard={true}
                            variant="detailed"
                          />
                        ))}
                      {/* Show count of filtered invalid citations */}
                      {invalidCitationsCount > 0 && !hasOrphanedCitations && (
                        <div className="text-xs text-slate-400 dark:text-neutral-500 italic pt-2 border-t border-slate-200 dark:border-neutral-700">
                          {invalidCitationsCount} citation(s) hidden (missing source linkage)
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center justify-center py-4 text-xs text-slate-400 dark:text-neutral-500">
                    <BookOpen className="h-4 w-4 mr-2 opacity-50" />
                    <span>No citations in this response</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageFooterTabs;
